import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  concatWith,
  forkJoin,
  map,
  mergeMap,
  of,
  switchMap
} from 'rxjs';
import { Logger } from '@app/utils/logger';
import { AuthActions } from '@components/auth/auth.actions';
import { SortDirection } from '@interfaces/schema';
import { ReportService } from '@pages/report/report.service';
import { SubscriptionService } from '@providers/api/subscription/subscription.service';
import { assistantSubscriptionActions } from './subscription.actions';
import { AssistantSubscriptionService } from './subscription.service';

/**
 * Ngrx effects of assistant-subscriptions.
 */
@Injectable()
export class AssistantSubscriptionEffects extends AssistantSubscriptionService {
  private readonly actions$ = inject(Actions);
  private readonly reportService = inject(ReportService);
  private readonly subscriptionService = inject(SubscriptionService);
  private readonly logger = Logger.getInstance(
    AssistantSubscriptionEffects.name
  );

  readonly check360Report$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        assistantSubscriptionActions.check360ReportStatus,
        assistantSubscriptionActions.userPurchasedProducts
      ),
      switchMap(() =>
        this.reportService
          .findAllReportTypes({
            where: {
              isDNA: true,
              isActive: true
            },
            include: ['kit-related', 'unlock-purchase-status'],
            withCount: false
            // limit: 1
          })
          .pipe(
            map((response) => {
              const data = Array.isArray(response.results)
                ? response.results
                : [];

              const txyReport = data.find((item) => item.code === 'DNA_360');

              let hasReleased = false;
              let hasActivated = false;
              let hasPurchased = false;
              let ableToUnlock = false;
              if (txyReport) {
                if (txyReport.reportHasReleased) {
                  hasReleased = true;
                  hasActivated = true;
                  hasPurchased = true;
                } else if (txyReport.userHasActivated) {
                  hasActivated = true;
                  hasPurchased = true;
                } else {
                  hasPurchased = false;
                  if (txyReport.unlockPurchaseStatus?.available) {
                    ableToUnlock = true;
                  } else {
                    ableToUnlock = false;
                  }
                }
              }

              return assistantSubscriptionActions.check360ReportStatusSuccess({
                result: {
                  hasActivated,
                  hasPurchased,
                  hasReleased,
                  ableToUnlock
                }
              });
            }),
            catchError((err: HttpErrorResponse) =>
              of(
                assistantSubscriptionActions.check360ReportStatusFailed({
                  error: {
                    message: err.message,
                    status: err.status
                  }
                })
              )
            )
          )
      )
    );
  });
  readonly fetchProductSubscriptions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(assistantSubscriptionActions.fetchProductSubscriptions),
      switchMap(() =>
        this.fetchSubscriptionProducts().pipe(
          map((response) => {
            return assistantSubscriptionActions.fetchProductSubscriptionsSuccess(
              {
                result: {
                  count: response.count ?? 0,
                  items: Array.isArray(response.results) ? response.results : []
                }
              }
            );
          }),
          catchError((err: HttpErrorResponse) =>
            of(
              assistantSubscriptionActions.fetchProductSubscriptionsFailed({
                error: {
                  message: err.message,
                  status: err.status
                }
              })
            )
          )
        )
      )
    );
  });

  readonly fetchUserSubscription$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        assistantSubscriptionActions.fetchUserSubscriptionDetailsAndUsages,
        assistantSubscriptionActions.userPurchasedProducts,
        AuthActions.login,
        AuthActions.switchProfile
      ),
      switchMap(() =>
        of().pipe(
          concatWith(
            forkJoin({
              detailsResponse: this.subscriptionService
                .getUserSubscriptionDetails({
                  includeSubscriptionAccessDetail: true,
                  includeSubscriptionPlan: true
                })
                .pipe(
                  catchError((error) => {
                    this.logger.error(
                      'Error in getUserSubscriptionDetails fetch, detail => ',
                      error
                    );
                    assistantSubscriptionActions.fetchUserSubscriptionDetailsFailed(
                      {
                        error: {
                          message: error?.message ?? 'Unknown error',
                          status: error?.status ?? 0,
                          statusText: error?.statusText ?? 'Unknown'
                        }
                      }
                    );
                    assistantSubscriptionActions.fetchUserSubscriptionDetailsAndUsagesFailed();
                    return of({
                      messages: '',
                      data: undefined
                    });
                  })
                ),
              usagesResponse: this.subscriptionService
                .getUserSubscriptionUsages({
                  includePermissionDetail: true
                })
                .pipe(
                  catchError((error) => {
                    this.logger.error(
                      'Error in getUserSubscriptionUsages fetch, detail => ',
                      error
                    );
                    assistantSubscriptionActions.fetchUserSubscriptionUsagesFailed(
                      {
                        error: {
                          message: error?.message ?? 'Unknown error',
                          status: error?.status ?? 0,
                          statusText: error?.statusText ?? 'Unknown'
                        }
                      }
                    );
                    assistantSubscriptionActions.fetchUserSubscriptionDetailsAndUsagesFailed();
                    return of({
                      messages: '',
                      data: {
                        count: 0,
                        results: []
                      }
                    });
                  })
                ),
              subscriptionChannelsResponse: this.fetchUserSubscriptionChannels({
                sort: {
                  createdAt: SortDirection.Desc
                }
              }).pipe(
                catchError((error) => {
                  this.logger.error(
                    'Error in getUserSubscriptionChannels fetch, detail => ',
                    error
                  );
                  assistantSubscriptionActions.fetchUserSubscriptionChannelFailed(
                    {
                      error: {
                        message: error.message,
                        status: error.status,
                        statusText: error.statusText
                      }
                    }
                  );
                  return of({
                    count: 0,
                    results: []
                  });
                })
              )
            }).pipe(
              mergeMap(
                ({
                  detailsResponse,
                  usagesResponse,
                  subscriptionChannelsResponse
                }) => {
                  return [
                    assistantSubscriptionActions.fetchUserSubscriptionDetailsSuccess(
                      {
                        data: detailsResponse.data
                      }
                    ),
                    assistantSubscriptionActions.fetchUserSubscriptionUsagesSuccess(
                      {
                        result: usagesResponse.data.results
                      }
                    ),
                    assistantSubscriptionActions.fetchUserSubscriptionChannelSuccess(
                      {
                        result: {
                          count: subscriptionChannelsResponse.count ?? 0,
                          items: Array.isArray(
                            subscriptionChannelsResponse.results
                          )
                            ? subscriptionChannelsResponse.results
                            : []
                        }
                      }
                    ),
                    assistantSubscriptionActions.fetchUserSubscriptionDetailsAndUsagesSuccess()
                  ];
                }
              )
            )
          )
        )
      )
    );
  });

  readonly fetchUserSubscriptionChannels$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        assistantSubscriptionActions.fetchUserSubscriptionChannel,
        assistantSubscriptionActions.userPurchasedProducts,
        AuthActions.login,
        AuthActions.switchProfile
      ),
      switchMap(() =>
        this.fetchUserSubscriptionChannels({
          sort: {
            createdAt: SortDirection.Desc
          }
        }).pipe(
          map((response) => {
            return assistantSubscriptionActions.fetchUserSubscriptionChannelSuccess(
              {
                result: {
                  count: response.count ?? 0,
                  items: Array.isArray(response.results) ? response.results : []
                }
              }
            );
          }),
          catchError((err: HttpErrorResponse) =>
            of(
              assistantSubscriptionActions.fetchUserSubscriptionChannelFailed({
                error: {
                  message: err.message,
                  status: err.status
                }
              })
            )
          )
        )
      )
    );
  });

  readonly resetOnAuthChange$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.login, AuthActions.logout, AuthActions.switchProfile),
      map(() => assistantSubscriptionActions.resetData())
    );
  });
}
